import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { getCurrentLicenseAllocation, LicenseAllocationResponse } from '../api/licenseAPI';
import { UserRole } from '../types/user';
import { logApiError } from '../utils/errorUtils';
import { LicenseEntitlements, getLicenseEntitlements } from '../utils/licenseUtils';
import { hasNoPractice } from '../utils/userUtils';

import { useAuth } from './AuthContext';

interface LicenseContextValue extends LicenseEntitlements {
  refresh: () => void;
  // Counts the license loads from the API, so data derived from the license (plan usage) can
  // reload when it changes, e.g. after useLicenseUpdates detects an upgrade.
  revision: number;
}

const defaultValue: LicenseContextValue = {
  tier: null,
  telehealthEnabled: false,
  seatsEntitled: null,
  aiEntitled: null,
  graceExpires: null,
  featureFlags: {},
  refresh: () => undefined,
  revision: 0,
};

const LicenseContext = createContext<LicenseContextValue>(defaultValue);

/**
 * Merge authoritative /api/license/current response into the entitlement
 * shape. When the server returns a real tier, it wins over the (often stale
 * or missing) JWT custom claim values.
 */
const applyAllocation = (
  base: LicenseEntitlements,
  allocation: LicenseAllocationResponse
): LicenseEntitlements => ({
  tier: allocation.tier ?? base.tier,
  telehealthEnabled: allocation.telehealthEnabled ?? base.telehealthEnabled,
  seatsEntitled: allocation.seatsEntitled ?? base.seatsEntitled,
  aiEntitled: allocation.aiEntitled ?? base.aiEntitled,
  graceExpires: allocation.gracePeriodEnd ?? base.graceExpires,
  featureFlags: {
    ...base.featureFlags,
    // Surface boolean feature flags from the license response so feature
    // gates that rely on `requiresFlag` still work.
    ...(allocation.featureFlags ?? {}),
    groupChat: allocation.groupChatEnabled,
    groupScheduling: allocation.groupSchedulingEnabled,
    fixedDateProgram: allocation.fixedDateProgramsEnabled,
    evergreenProgram: allocation.evergreenProgramsEnabled,
    smsBroadcast: allocation.smsBroadcastsEnabled,
    unifiedTeamCalendar: allocation.unifiedTeamCalendarEnabled,
    practiceBranding: allocation.practiceBrandingEnabled,
    billingManagement: allocation.billingManagementEnabled,
    auditLogs: allocation.auditLogsEnabled,
  },
});

export const LicenseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, currentUser } = useAuth();
  const [entitlements, setEntitlements] = useState<LicenseEntitlements>(getLicenseEntitlements);
  const [revision, setRevision] = useState(0);

  const refresh = useCallback(async () => {
    // Always start from the JWT-derived defaults so we have something
    // rendered while the API call is in flight.
    const fromClaims = getLicenseEntitlements();

    setEntitlements(fromClaims);

    if (!isAuthenticated) return;

    // /api/license/current is scoped to a practice. Clients aren't members of
    // a practice in the licensing sense — calling it returns 403 and
    // pollutes the dashboard with errors. JWT-derived defaults are the right
    // shape for a Client: ClientDashboard does not consume license tier.
    // A SystemAdmin has no practice at all (HA-446), so the request can only 403.
    if (currentUser?.role === UserRole.Client || hasNoPractice(currentUser?.role)) return;

    try {
      const allocation = await getCurrentLicenseAllocation();

      setEntitlements(applyAllocation(fromClaims, allocation));
      setRevision((previous) => previous + 1);
    } catch (error) {
      // Keep the JWT-derived values as a fallback on failure. The old
      // behavior was "JWT only" — we're no worse off than before.
      logApiError('LicenseContext.refresh', error);
    }
  }, [isAuthenticated, currentUser?.role]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <LicenseContext.Provider
      value={{
        ...entitlements,
        refresh,
        revision,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
};

export const useLicense = (): LicenseContextValue => {
  return useContext(LicenseContext);
};
